<template>
  <div class="mt-15 container-inner">
    <h3 class="title-type-2">
      <strong>{{ propInventory.name }}</strong> 상품 세부 설정
    </h3>
    <div class="mb-8">
      <h4 class="title-type-3">
        지면설명
        <span class="attention-coment-1 ml-2">
          <v-icon>mdi-alert-circle</v-icon>
          {{ propInventory.note }}
        </span>
      </h4>
    </div>
    <v-form ref="form">
      <ModeAndCourseOption
        v-if="inventoryOptions.mode_and_course"
        :propOption="inventoryOptions.mode_and_course"
        :propLineItem="lineItem"
      />
      <HoleOption
        v-if="inventoryOptions.hole"
        :propOption="inventoryOptions.hole"
        :propLineItem="lineItem"
        :selectedOptionItem="selectedOptionItem"
      />
      <ResourceOption
        v-if="resourceOptions.length > 0"
        :propInventory="propInventory"
        :propLineItem="lineItem"
        :propResourceOptions="resourceOptions"
        :selectedOptionItem="selectedOptionItem"
        :propHoleOption="inventoryOptions.hole"
        :propAdvertiserId="propAdvertiserId"
        :propLinkOption="inventoryOptions.link"
      />
      <v-btn
        large
        depressed
        max-height="40px"
        color="#fecd06"
        class="button-type-1 ml-4"
        @click.prevent="saveLineItem"
      >
        저장
      </v-btn>
    </v-form>
    <SystemAlert v-model="systemAlert.show">
      <div slot="noti-text">{{ systemAlert.message }}</div>
      <div slot="noti-btns">
        <v-btn
          large
          depressed
          max-height="40px"
          min-width="104px"
          color="#fecd06"
          class="button-type-1"
          @click.stop="systemAlert.continue"
        >
          확인
        </v-btn>
        <v-btn
          v-if="systemAlert.abort"
          large
          depressed
          max-height="40px"
          min-width="104px"
          color="#fff"
          class="button-type-1 ml-2"
          @click.stop="systemAlert.abort"
        >
          취소
        </v-btn>
      </div>
    </SystemAlert>
  </div>
</template>

<script>
import bus from '@/utils/bus';
import { toISOFormattedDate } from '@/utils/validation';
import { getDetailCampaignLineItem } from '@/api/campaign';
import { getOptionListByInventory } from '@/api/inventory';
import { updateLineitem } from '@/api/v2/lineitem';
import ModeAndCourseOption from '@/components/campaign/ModeAndCourseOption.vue';
import HoleOption from '@/components/campaign/HoleOption.vue';
import ResourceOption from '@/components/campaign/ResourceOption.vue';
import SystemAlert from '~@/modal/SystemAlert.vue';
import _ from 'lodash';

export default {
  props: { propInventory: Object, propAdvertiserId: String },
  components: {
    ModeAndCourseOption,
    HoleOption,
    ResourceOption,
    SystemAlert,
  },
  data: () => ({
    inventoryOptions: {},
    resourceOptions: [],
    lineItem: null,
    selectedOptionItem: null,
    systemAlert: {
      show: false,
      message: '',
      continue: null,
      abort: null,
      reloadLineitem: null,
    },
  }),
  computed: {
    defaultStartAt() {
      return this.propInventory.start_at;
    },
    defaultEndAt() {
      return this.propInventory.end_at;
    },
  },
  methods: {
    async getDetailLineItem() {
      const { data } = await getDetailCampaignLineItem(
        this.$route.params.id,
        this.propInventory.id,
        this.propInventory.lineitems[0].id,
      );
      this.lineItem = data.row.medias[0].inventories[0].lineitems[0];
      return this.lineItem;
    },
    async getOptions() {
      const { data } = await getOptionListByInventory(this.propInventory.id);

      data.list.forEach(option => {
        this.inventoryOptions[option.key] = option;
      });
      this.resourceOptions = [
        this.inventoryOptions.image,
        this.inventoryOptions.video,
      ].filter(o => !!o);
    },
    async saveLineItem() {
      bus.$emit('start:spinner');
      const values = {};
      try {
        for (const el of this.$refs.form.$children) {
          if (!el.validate || !el.getValues) continue;

          if (!el.validate()) {
            return this.systemAlertHandle({
              show: true,
              message: '필수값이 모두 입력되지 않았습니다.',
            });
          }

          Object.assign(values, await el.getValues());
        }

        const param = {
          json: {
            mode: values['mode'],
            competition_id: values['competition_id'],
            cc_id: values['cc_id'],
            course_group_id: values['course_group_id'],
            use_xml: values['use_xml'],
          },
          link_type: null,
          link_url: null,
          lri: {
            item_id: this.selectedOptionItem.id,
            resources: values.resources.map(resource => ({
              id: resource.id,
              name: resource.name,
              type: resource.type,
              is_valid: resource.is_valid,
              start_at: toISOFormattedDate(new Date(resource.start_at)),
              end_at: toISOFormattedDate(new Date(resource.end_at)),
              hash: resource.hash,
              file: _.get(
                resource,
                'newFile.permission.uploadImagePath',
                resource.file,
              ),
              link_type: resource.link_type || null,
              link: resource.link || null,
            })),
          },
        };
        const { data } = await updateLineitem(this.lineItem.id, param);
        if (data.result === 'success') {
          this.systemAlertHandle({
            show: true,
            message: '저장되었습니다.',
            reloadInventory: true,
            reloadLineitem: true,
          });
        }
      } catch (e) {
        console.log(e);
      } finally {
        bus.$emit('end:spinner');
      }
    },
    optionItemSelect(optionItem) {
      this.selectedOptionItem = optionItem;
    },
    systemAlertHandle(data) {
      const defaultSystemAlert = {
        show: false,
        message: '',
        continue: null,
        abort: null,
        reloadInventory: null,
        reloadLineitem: null,
      };
      this.systemAlert.show = data.show;
      this.systemAlert.message = data.message;
      this.systemAlert.continue = async () => {
        this.systemAlert = defaultSystemAlert;

        if (data.continue) await data.continue();
        if (data.reloadInventory) this.$emit('reload');
        if (data.reloadLineitem) await this.getDetailLineItem();
      };
      if (data.abort) {
        this.systemAlert.abort = async () => {
          this.systemAlert = defaultSystemAlert;
          await data.abort();
        };
      }
    },
  },
  async created() {
    bus.$on('option_item:select', this.optionItemSelect);
    bus.$on('system_alert:handle', this.systemAlertHandle);

    await this.getDetailLineItem();
    await this.getOptions();
  },
  destroyed() {
    bus.$off('option_item:select', this.optionItemSelect);
    bus.$off('system_alert:handle', this.systemAlertHandle);
  },
};
</script>
